import { isStrongPassword } from '../../../common/utils/password-policy';
import { PasswordRules } from '../auth/components/AccountPage';
import { useState } from 'react';
import { Check, Eye, EyeOff, Globe, Lock, Mail, TriangleAlert, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ValidatedInput } from '../../../components/design-system/auth';
import { JourneyFields, SignedInTokens, SignupConfig, WebsiteCheck, useSignupApi } from '../../../common/hooks/use-signup-api.hook';
import { SignupErrorCode, readSignupError } from '../../../modules/signup/signup-errors';
import { Turnstile } from '../../../modules/signup/Turnstile';
import { useHumanCheck } from '../../../modules/signup/use-human-check';
import { SignupError } from './SignupError';
import { LegalNote } from './LegalNote';
import { GoogleContinue } from '../../../components/design-system/auth';

export const MIN_PASSWORD = 8;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Nina Stone" → firstname Nina, lastname Stone; one word is a first name. */
export function splitName(name: string): { firstname?: string; lastname?: string } {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return {};
    return { firstname: parts[0], lastname: parts.slice(1).join(' ') || undefined };
}

/** What the website check found, with a way back to change it. */
const SiteCard = ({ site, onChange }: { site: WebsiteCheck; onChange: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="animate-form-item animate-delay-1 rounded-xl border bg-card p-3.5" data-testid="signup-site">
            <div className="flex items-start gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background">
                    <Globe className="size-4 text-muted-foreground" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium" dir="ltr">{site.domain}</p>
                    <p className="truncate text-xs text-muted-foreground">
                        {[site.title, site.platform].filter(Boolean).join(' · ') || t('SIGNUP.SITE.FOUND')}
                    </p>
                </div>
                <Button type="button" variant="ghost" size="sm" className="shrink-0 text-muted-foreground" onClick={onChange}>
                    {t('SIGNUP.SITE.CHANGE')}
                </Button>
            </div>
            {!site.reachable && (
                <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-warning" data-testid="signup-site-unreachable">
                    <TriangleAlert className="mt-px size-3.5 shrink-0" />
                    {t('SIGNUP.SITE.UNREACHABLE')}
                </p>
            )}
        </div>
    );
};

/**
 * Step 2: the account itself — Google in one click, or an email and a
 * password. The human check runs underneath; people only ever see it when
 * it is slow ("Checking you're human…") or when Turnstile wants a click.
 */
export const AccountStep = ({
    website,
    site,
    config,
    human,
    startedAt,
    journey = {},
    onChangeSite,
    onSignedIn,
}: {
    website: string;
    site: WebsiteCheck;
    config: SignupConfig | null;
    human: ReturnType<typeof useHumanCheck>;
    startedAt: number;
    /** Sent with the account so the API joins it to what came before (docs/plg-review.md). */
    journey?: JourneyFields;
    onChangeSite: () => void;
    onSignedIn: (tokens: SignedInTokens, method: 'email' | 'google') => Promise<void>;
}) => {
    const { t, i18n } = useTranslation();
    const api = useSignupApi();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // The honeypot. Nobody sees it; a bot filling every field fills this too.
    const [companyUrl, setCompanyUrl] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<SignupErrorCode | null>(null);

    const emailValid = EMAIL.test(email.trim());
    // The API's rules (10–64 characters, lower, upper, digit, symbol, no common word).
    const passwordValid = isStrongPassword(password);
    const locale = (i18n?.language || 'en').slice(0, 2);

    const attempt = async (send: () => Promise<SignedInTokens>, method: 'email' | 'google' = 'email') => {
        setBusy(true);
        setError(null);
        try {
            const tokens = await send();
            await onSignedIn(tokens, method);
        } catch (e) {
            setError(await readSignupError(e));
            // Every proof is single-use: the next attempt needs a new one.
            human.renew();
            setBusy(false);
        }
    };

    const submit = async () => {
        if (!emailValid || !passwordValid) {
            setShowValidation(true);
            return;
        }
        setShowValidation(false);
        await attempt(async () => {
            const proof = await human.waitForProof();
            return api.register({
                website,
                email: email.trim(),
                password,
                ...splitName(name),
                locale,
                company_url: companyUrl,
                startedAt,
                ...journey,
                ...proof,
            });
        });
    };

    const withGoogle = async (credential?: string) => {
        if (!credential) {
            setError('GOOGLE_FAILED');
            return;
        }
        await attempt(async () => {
            // Google already vouches for a person; the puzzle goes along when there is one.
            const proof: any = human.mode === 'pow' ? await human.waitForProof(10_000) : {};
            return api.google({ credential, website, locale, ...journey, ...(proof.challenge ? { challenge: proof.challenge } : {}) });
        }, 'google');
    };

    const googleClientId = config?.googleClientId;

    return (
        <div data-testid="signup-account-step">
            <SiteCard site={site} onChange={onChangeSite} />

            <GoogleContinue
                clientId={googleClientId}
                onCredential={withGoogle}
                onError={() => setError('GOOGLE_FAILED')}
                context="signup"
                locale={locale}
                className="mt-6"
                testId="signup-google"
            />

            <form
                noValidate
                aria-busy={busy}
                className={googleClientId ? undefined : 'mt-6'}
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <fieldset disabled={busy} className="contents">
                    <ValidatedInput
                        label={t('SIGNUP.ACCOUNT.EMAIL')}
                        placeholder="hello@yourstore.com"
                        value={email}
                        onChange={(v) => {
                            setEmail(v);
                            setShowValidation(false);
                        }}
                        type="email"
                        autoComplete="email"
                        error={showValidation && !emailValid}
                        errorMessage={t('SIGNUP.ACCOUNT.EMAIL_INVALID')}
                        startIcon={<Mail />}
                        className="animate-form-item animate-delay-2"
                    />
                    <ValidatedInput
                        label={t('SIGNUP.ACCOUNT.PASSWORD')}
                        placeholder="••••••••"
                        value={password}
                        onChange={(v) => {
                            setPassword(v);
                            setShowValidation(false);
                        }}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        error={showValidation && !passwordValid}
                        errorMessage={t('SIGNUP.ACCOUNT.PASSWORD_WEAK_HINT')}
                        startIcon={<Lock />}
                        endIcon={
                            <button
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                aria-label={showPassword ? t('AUTH.HIDE_PASSWORD') : t('AUTH.SHOW_PASSWORD')}
                                className="flex items-center transition-colors hover:text-foreground"
                            >
                                {showPassword ? <EyeOff /> : <Eye />}
                            </button>
                        }
                        className="animate-form-item animate-delay-3 mt-4"
                    />
                    {password.length > 0 && <PasswordRules password={password} />}
                    <ValidatedInput
                        label={t('SIGNUP.ACCOUNT.NAME')}
                        placeholder={t('SIGNUP.ACCOUNT.NAME_PLACEHOLDER')}
                        value={name}
                        onChange={setName}
                        autoComplete="name"
                        startIcon={<UserIcon />}
                        className="animate-form-item animate-delay-3 mt-4"
                    />

                    {/* Off-screen rather than display:none, which some bots skip. */}
                    <div aria-hidden="true" className="pointer-events-none absolute -start-[10000px] top-auto size-px overflow-hidden">
                        <label>
                            Company URL
                            <input
                                type="text"
                                name="company_url"
                                tabIndex={-1}
                                autoComplete="off"
                                value={companyUrl}
                                onChange={(e) => setCompanyUrl(e.target.value)}
                                data-testid="signup-honeypot"
                            />
                        </label>
                    </div>

                    {human.mode === 'turnstile' && config?.turnstileSiteKey && (
                        <Turnstile
                            siteKey={config.turnstileSiteKey}
                            language={locale}
                            onToken={human.onTurnstileToken}
                            onError={human.onTurnstileError}
                            resetRef={human.resetTurnstile}
                            className="mt-5 flex justify-center"
                        />
                    )}

                    {error && <SignupError code={error} email={email.trim()} />}

                    <Button type="submit" variant="outline" className="animate-form-item animate-delay-4 mt-6 h-11 w-full" data-testid="signup-submit">
                        {busy ? <Spinner aria-label={t('SIGNUP.ACCOUNT.CREATING')} /> : t('SIGNUP.ACCOUNT.SUBMIT')}
                    </Button>

                    <HumanNote mode={human.mode} state={human.state} />
                </fieldset>
            </form>

            <LegalNote />
        </div>
    );
};


/** A quiet line under the button: only while the check runs, or if it could not. */
const HumanNote = ({ mode, state }: { mode: string; state: string }) => {
    const { t } = useTranslation();
    if (mode !== 'pow') return null;
    if (state === 'working' || state === 'idle') {
        return (
            <p className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground" role="status" data-testid="signup-human-working">
                <Spinner className="size-3" aria-hidden />
                {t('SIGNUP.HUMAN.CHECKING')}
            </p>
        );
    }
    if (state === 'ready') {
        return (
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground" data-testid="signup-human-ready">
                <Check className="size-3" aria-hidden />
                {t('SIGNUP.HUMAN.DONE')}
            </p>
        );
    }
    return (
        <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="signup-human-failed">
            {t('SIGNUP.HUMAN.FAILED')}
        </p>
    );
};
